<script setup lang="ts">
/**
 * The enterprise's notes and how they relate, as a list (#690, Operations → Memory). Each entry is one note: what
 * kind of knowledge it is, what it says, the labels it is filed under, and its relationships in words. A correction
 * supersedes the note it replaced, notes share a task or a team, and similar wording is never proof. One memory, no
 * vaults: labels are written on the notes, never drawn as walls around them.
 *
 * The same memory is drawn as a field by ZhKnowledgeConstellation. A canvas cannot be read aloud or tabbed
 * through, so this list is the rendering every reader can use, and a page mounts it beside the constellation.
 *
 * Data in (lib/surfaces.ts KnowledgeNodeView and KnowledgeEdgeView, shaped after #690's MemoryGraphNode and
 * MemoryGraphEdge), drawing out. The selected note, the one being read, is cyan: memory's data colour. `select`
 * says which note was picked; `drawn` makes the notes a picture rather than buttons.
 *
 * What a screen of its own needs, all additive:
 * - every entry carries its note's identity and state (`data-list-node-id`, `data-knowledge-type`,
 *   `data-correction-state`);
 * - `typeWords` / `relationWords` say a kind of note or a relationship in the screen's own sentences;
 * - `activatorTestId` names the button that opens a note;
 * - the `actions` slot draws a note's own controls beside its entry. A button may not sit inside a button, so once
 *   there are actions the entry stops being the button: the note's body is.
 */
import { computed } from 'vue'
import { otherEnd } from '../lib/knowledge'
import type { KnowledgeRelation, KnowledgeType } from '../lib/surfaces'
import {
    KNOWLEDGE_RELATION_WORD,
    KNOWLEDGE_TYPE_WORD,
    type KnowledgeEdgeView,
    type KnowledgeNodeView,
} from '../lib/surfaces'

const props = defineProps<{
    nodes: readonly KnowledgeNodeView[]
    edges: readonly KnowledgeEdgeView[]
    selectedId?: string | null
    label?: string
    drawn?: boolean
    /** The words to say each kind of note in, over the kit's own; a kind it does not name keeps the kit's word. */
    typeWords?: Partial<Record<KnowledgeType, string>>
    /** The words to say each relationship in, over the kit's own. */
    relationWords?: Partial<Record<KnowledgeRelation, string>>
    /** The test id the button that opens a note wears. */
    activatorTestId?: string
}>()
const emit = defineEmits<{ select: [id: string] }>()

function typeWord(type: KnowledgeType): string {
    return props.typeWords?.[type] ?? KNOWLEDGE_TYPE_WORD[type]
}

function relationWord(type: KnowledgeRelation): string {
    return props.relationWords?.[type] ?? KNOWLEDGE_RELATION_WORD[type]
}

const byId = computed(() => new Map(props.nodes.map(node => [node.id, node])))

function relationsOf(id: string): string[] {
    return props.edges.flatMap(edge => {
        const other = otherEnd(edge, id)
        const note = other ? byId.value.get(other) : undefined
        if (!note) return []
        const word = edge.type === 'supersedes' && edge.target === id ? 'Superseded by' : relationWord(edge.type)
        return [`${word}: ${note.body}`]
    })
}

function pick(id: string): void {
    if (!props.drawn) emit('select', id)
}
</script>

<template>
    <div class="zh-kg" :data-actions="$slots.actions ? 'on' : undefined" :aria-hidden="drawn ? 'true' : undefined">
        <ol class="zh-kg__list" :aria-label="label ?? 'What the company knows'">
            <li
                v-for="node in nodes"
                :key="node.id"
                class="zh-kg__item"
                :data-list-node-id="node.id"
                :data-knowledge-type="node.knowledgeType"
                :data-correction-state="node.correctionState"
                :data-type="node.knowledgeType"
                :data-state="node.correctionState"
                :data-selected="node.id === selectedId ? 'true' : undefined"
            >
                <!-- With actions the entry holds them, and the note's body is the button that opens it. -->
                <div v-if="$slots.actions" class="zh-kg__item-main">
                    <span class="zh-kg__type">{{ typeWord(node.knowledgeType) }}</span>
                    <component
                        :is="drawn ? 'span' : 'button'"
                        :type="drawn ? undefined : 'button'"
                        class="zh-kg__activator"
                        :data-testid="drawn ? undefined : activatorTestId"
                        :aria-pressed="drawn ? undefined : node.id === selectedId"
                        @click="pick(node.id)"
                        ><span class="zh-kg__body">{{ node.body }}</span></component
                    >
                    <span v-if="node.labels.length" class="zh-kg__label">{{
                        node.labels.map(label => label.name).join(', ')
                    }}</span>
                </div>
                <component
                    :is="drawn ? 'div' : 'button'"
                    v-else
                    :type="drawn ? undefined : 'button'"
                    class="zh-kg__item-main"
                    :data-testid="drawn ? undefined : activatorTestId"
                    :aria-pressed="drawn ? undefined : node.id === selectedId"
                    @click="pick(node.id)"
                >
                    <span class="zh-kg__type">{{ typeWord(node.knowledgeType) }}</span>
                    <span class="zh-kg__body">{{ node.body }}</span>
                    <span v-if="node.labels.length" class="zh-kg__label">{{
                        node.labels.map(label => label.name).join(', ')
                    }}</span>
                </component>
                <div v-if="$slots.actions" class="zh-kg__actions"><slot name="actions" :node="node" /></div>
                <ul v-if="relationsOf(node.id).length" class="zh-kg__relations">
                    <li v-for="line in relationsOf(node.id)" :key="line">{{ line }}</li>
                </ul>
            </li>
        </ol>
    </div>
</template>

<style scoped>
button.zh-kg__item-main {
    cursor: pointer;
}

button.zh-kg__item-main:focus-visible {
    outline: 2px solid var(--electric-cyan);
    outline-offset: 2px;
}

/* A note with actions: its body is the button, and that button's hit area still covers its whole entry. */
.zh-kg__activator {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
}

button.zh-kg__activator {
    cursor: pointer;
}

button.zh-kg__activator::after {
    content: "";
    position: absolute;
    inset: 0;
}

button.zh-kg__activator:focus-visible {
    outline: 2px solid var(--electric-cyan);
    outline-offset: 2px;
}

div.zh-kg__item-main {
    position: relative;
}

/* Above the activator's hit area, so taking an action is never taken for opening the note. */
.zh-kg__actions {
    position: relative;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;
}

.zh-kg__actions:empty {
    display: none;
}

.zh-kg__item > .zh-kg__actions {
    padding: 0 0.75rem 0.625rem;
}

.zh-kg__type {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.625rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

/* The kind's mark: a fact is a filled square, an observation an open one, a preference an open circle, a
   correction a filled diamond, an inference a dashed square — drawn from a pattern, never proof. */
.zh-kg__type::before {
    content: "";
    width: 0.5rem;
    height: 0.5rem;
    border: 1.5px solid var(--ink-2);
}

[data-type="fact"] .zh-kg__type::before {
    background: var(--ink-2);
}

[data-type="preference"] .zh-kg__type::before {
    border-radius: 50%;
}

[data-type="correction"] .zh-kg__type::before {
    background: var(--dirty-white);
    border-color: var(--dirty-white);
    rotate: 45deg;
    scale: 0.85;
}

[data-type="inference"] .zh-kg__type::before {
    border-style: dashed;
}

.zh-kg__body {
    display: -webkit-box;
    overflow: hidden;
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.35;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
}

.zh-kg__label {
    color: var(--ink-3);
    font-size: 0.75rem;
}

/* Superseded: what it said before a correction, still on the record, struck. */
[data-state="corrected"] .zh-kg__body {
    color: var(--ink-3);
    text-decoration: line-through;
    text-decoration-color: var(--line);
}

[data-state="excluded"] {
    opacity: 0.45;
}

.zh-kg__list,
.zh-kg__relations {
    margin: 0;
    padding: 0;
    list-style: none;
}

.zh-kg__list {
    display: grid;
    gap: 0.5rem;
}

.zh-kg__item {
    border: 1px solid var(--line);
    background: var(--void-black);
}

.zh-kg__item[data-selected] {
    border-color: var(--electric-cyan);
}

.zh-kg__item[data-type="inference"],
.zh-kg__item[data-state="corrected"] {
    border-style: dashed;
}

.zh-kg__item-main {
    display: grid;
    gap: 0.25rem;
    width: 100%;
    padding: 0.625rem 0.75rem;
    border: 0;
    background: none;
    color: var(--ink-1);
    font: inherit;
    text-align: left;
}

.zh-kg__relations {
    display: grid;
    gap: 0.125rem;
    padding: 0 0.75rem 0.625rem;
    color: var(--ink-3);
    font-size: 0.75rem;
}
</style>
